import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { AssetsAction } from 'us.collection.case/actions';
import { useTranslation } from 'react-i18next';
import Common from 'us.common';
import { Formik } from 'formik';
import PostalArea from 'us.common/containers/PostalArea/PostalArea';
import RealEstateSchema from './validations';
import { SaveRealEstateAssets } from './SaveRealEstateAssets';
import { UpdateRealEstateAssets } from './UpdateRealEstateAssets';
import { IMetaData } from 'us.collection/interfaces';
import { PostalCodeInput, SelectMunicipal } from 'us.common/components';
import { IDataModel } from 'us.collection.case/reducers/Dashboard/Interfaces';
import { RootState } from 'us.helper/types';
import { DEFAULT_COUNTRY_CODE, DEFAULT_POSTAL_CODE_SEARCH_BY } from 'us.common/constants';

const {
	$Input,
	$DatePicker,
	$Form,
	$Button,
	$Col,
	$Drawer,
	$Row,
	$Skeleton,
	$TextArea,
	$Popconfirm,
	$InputAmount,
	$InputNumber,
} = Common.Components;

interface IRealEstate {
	asset: any;
	isEditing: boolean;
	postalCodeDetails?: any;
	showOnly?: boolean;
}

interface IBase extends IRealEstate {
	postalAreas: any;
	currentLanguage: string;
	currentCurrency: string;
	currentDateFormat: string;
	metaData: IDataModel<IMetaData>;
	addRealEstateAssets: any;
	changeDrawerInfo: any;
	drawerInfo: any;
	getRealEstateAssets: any;
	realEstateAssets: any;
	resetRealEstateAssets: any;
	updateRealEstateAssets: any;
	isREFetching: boolean;
	drawerName: any;
	getPostalDetails: any;
}

const RealEstate: React.FC<IBase> = (props) => {
	const { t } = useTranslation(['US.COLLECTION.CASE'], {
		useSuspense: true,
	});
	const {
		postalAreas,
		currentLanguage,
		currentCurrency,
		currentDateFormat,
		metaData,
		addRealEstateAssets,
		changeDrawerInfo,
		drawerInfo,
		getRealEstateAssets,
		realEstateAssets,
		resetRealEstateAssets,
		updateRealEstateAssets,
		isREFetching,
		drawerName,
		showOnly,
		getPostalDetails,
	} = props;

	const { debtorEntNumber, cid } = metaData.data ?? {};

	const [postalCodeId, setPostalCodeId] = useState(0);
	const [municipalityId, setMunicipalityId] = useState(0);

	const handleCancel = (resetForm: any) => {
		changeDrawerInfo({
			title: '',
			visible: false,
		});
		resetForm();
		resetRealEstateAssets();
	};

	const [postalDrawerVisible, setPostalDrawerVisible] = useState(false);
	const [municipalityDrawerVisible, setMunicipalityDrawerVisible] =
		useState(false);
	const openPostalArea = () => {
		setPostalDrawerVisible(true);
	};
	const closePostalArea = () => {
		setPostalDrawerVisible(false);
	};
	const openMunicipalityArea = () => {
		setMunicipalityDrawerVisible(true);
	};
	const closeMunicipalityArea = () => {
		setMunicipalityDrawerVisible(false);
	};
	const postalAreaClose = () => {
		setPostalDrawerVisible(false);
	};

	const handlePostalDoubleClick = (
		selectedPostalAreaDetail: any,
		rest: any,
		values: any
	) => {
		setPostalDrawerVisible(false);
		let selectedPostalInfo = postalAreas?.filter(
			(p: any) =>
				p.postalCode ==
				selectedPostalAreaDetail?.PostalCode
		);
		let postalCodeId =
			selectedPostalInfo.length > 0
				? selectedPostalInfo[0].postalCodeId
				: 0;
		let municipalityId =
			selectedPostalInfo.length > 0
				? selectedPostalInfo[0].countyCode
				: 0;
		setPostalCodeId(postalCodeId);
		setMunicipalityId(municipalityId);
		rest.setValues(
			{
				...values,
				postalCode: selectedPostalAreaDetail?.PostalCode,
				postalArea: selectedPostalAreaDetail?.City,
				municipalityCode:
					selectedPostalAreaDetail?.MunicipalityCode,
				municipalityArea:
					selectedPostalAreaDetail?.MunicipalityName,
			},
			true
		);
	};

	const descriptionTextAreaRef = React.useRef<any>();

	useEffect(() => {
		descriptionFocus();
	}, []);

	useEffect(() => {
		if (drawerInfo?.isEdit) {
			getRealEstateAssets(drawerInfo?.assetId);
		}
	}, [drawerInfo?.isEdit]);

	const handleMunicipalityDoubleClick = (
		municipalityDetails: any,
		rest: any
	) => {
		rest.setFieldValue(
			'municipalityCode',
			municipalityDetails?.municipalityCode
		);
		rest.setFieldValue(
			'municipalityArea',
			municipalityDetails?.municipalityName
		);
		rest.setFieldValue('postalCode', '');
		rest.setFieldValue('postalArea', '');
	};

	const descriptionFocus = () => {
		typeof descriptionTextAreaRef?.current !== 'undefined' &&
			descriptionTextAreaRef.current.focus();
	};

	return (
		<Formik
			initialValues={{
				...realEstateAssets,
				gnr:
					realEstateAssets.gnr == 0
						? ''
						: realEstateAssets.gnr,
				bnr:
					realEstateAssets.bnr == 0
						? ''
						: realEstateAssets.bnr,
				fnr:
					realEstateAssets.fnr == 0
						? ''
						: realEstateAssets.fnr,
				snr:
					realEstateAssets.snr == 0
						? ''
						: realEstateAssets.snr,
			}}
			//initialTouched={{postalCode: true}}
			enableReinitialize
			validationSchema={RealEstateSchema}
			onSubmit={(values: any, actions: any) => {
				actions.setTouched({ postalCode: true }, true);
				if (!drawerInfo?.isEdit) {
					let entityInfo = {
						entityId: debtorEntNumber,
						entityType: 'DEB',
					};
					let payload = SaveRealEstateAssets.call(
						{
							...values,
							...entityInfo,
							postalCodeId,
							municipalityId,
						}
					);
					addRealEstateAssets(payload);
				} else {
					setPostalCodeId(
						realEstateAssets.postalId
					);
					let entityInfo = {
						entityId: debtorEntNumber,
						entityType: 'DEB',
						assetId: drawerInfo?.assetId,
					};
					let postalCodeId =
						realEstateAssets.postalId;
					let municipalityId =
						realEstateAssets.municipalityId;
					let payload =
						UpdateRealEstateAssets.call({
							...values,
							...entityInfo,
							postalCodeId,
							municipalityId,
						});
					updateRealEstateAssets(payload);
				}
				changeDrawerInfo({
					title: '',
					visible: false,
				});
				resetRealEstateAssets();
				actions.resetForm();
			}}>
			{({
				values,
				handleChange,
				handleBlur,
				handleSubmit,
				isSubmitting,
				isValidating,
				resetForm,
				...restProps
			}: any) => (
				<>
					<$Form>
						<$Skeleton
							loading={
								isREFetching &&
								drawerInfo?.isEdit
							}
							active
							paragraph={{ rows: 2 }}>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={24}>
									<$TextArea
										name='description'
										autoSize={{
											minRows: 2,
											maxRows: 4,
										}}
										size='small'
										label={t(
											'US.COLLECTION.COMMON:COMMON.DESCRIPTION'
										)}
										required
										ref={
											descriptionTextAreaRef
										}
										tabIndex={
											1
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
							</$Row>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={12}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.OWNEDPART/SHARE'
										)}
										name='ownedPart'
										// required
										tabIndex={
											2
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={12}>
									<$InputNumber
										size='small'
										label={t(
											'US.COLLECTION.COMMON:COMMON.DEBTORNO'
										)}
										name='debtorNo'
										value={
											cid
										}
										disabled
									/>
								</$Col>
							</$Row>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={12}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.ADDRESS_LINE_1'
										)}
										name='address1'
										tabIndex={
											3
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={6}>
									<$InputAmount
										size='small'
										className='w-100'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.ESTIMATEDVALUE'
										)}
										name='estimatedValue'
										currentLanguage={
											currentLanguage
										}
										currentCurrency={
											currentCurrency
										}
										value={
											values?.estimatedValue
										}
										tabIndex={
											10
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={6}>
									<$InputAmount
										size='small'
										className='w-100'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.VALUATION'
										)}
										name='valuationAmount'
										currentLanguage={
											currentLanguage
										}
										currentCurrency={
											currentCurrency
										}
										value={
											values?.valuationAmount
										}
										tabIndex={
											11
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
							</$Row>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={12}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.ADDRESS_LINE_2'
										)}
										name='address2'
										tabIndex={
											4
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={6}>
									<$DatePicker
										label={t(
											'US.COLLECTION.CASE:REALESTATE.VALUATIONDATE'
										)}
										name='valuationDate'
										size='small'
										style={{
											width: '100%',
										}}
										format={
											currentDateFormat
										}
										value={
											values?.valuationDate &&
											moment(
												values?.valuationDate
											).isValid()
												? moment(
														values?.valuationDate
												  )
												: undefined
										}
										allowClear
										tabIndex={
											12
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
							</$Row>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={6}>
									{!showOnly && (
										<a
											className='d-block'
											style={{
												paddingBottom:
													'0.13rem',
											}}
											onClick={() =>
												openPostalArea()
											}>
											<span className='required'>
												*
											</span>{' '}
											<strong>
												{t(
													'US.COLLECTION.COMMON:ENTITYSELECTION.POSTALCODE'
												)}
											</strong>
										</a>
									)}
									{showOnly && (
										<strong>
											{t(
												'US.COLLECTION.COMMON:ENTITYSELECTION.POSTALCODE'
											)}
										</strong>
									)}
									<PostalCodeInput
										name="postalCode"
										size="small"
										value={values.postalCode}
										defaultCountryCode={DEFAULT_COUNTRY_CODE}
										useValueInput={true}
										onSearch={({ countryCode, searchText, formData }) =>
											getPostalDetails?.({
												countryCode,
												searchText,
												values: formData,
												searchBy: DEFAULT_POSTAL_CODE_SEARCH_BY,
											})
										}
										onValueChange={(value, { setValues, values }) => {
											setValues({
												...values,
												postalCode: value,
												postalArea: "",
												municipalityCode: "",
												municipalityArea: ""
											});
										}}

									/>
								</$Col>
								<$Col span={6}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.COMMON:ENTITYSELECTION.POSTALAREA'
										)}
										name='postalArea'
										value={
											values.postalArea
										}
										tabIndex={
											6
										}
										disabled
									/>
								</$Col>
								<$Col span={3}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.GNR'
										)}
										name='gnr'
										type='text'
										onChange={(
											e: any
										) => {
											const reg =
												/^-?[0-9]*(\.[0-9]*)?$/;
											if (
												!isNaN(
													e
														.target
														.value
												) &&
												reg.test(
													e
														.target
														.value
												)
											) {
												handleChange(
													e
												);
											}
										}}
										tabIndex={
											13
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={3}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.BNR'
										)}
										name='bnr'
										onChange={(
											e: any
										) => {
											const reg =
												/^-?[0-9]*(\.[0-9]*)?$/;
											if (
												!isNaN(
													e
														.target
														.value
												) &&
												reg.test(
													e
														.target
														.value
												)
											) {
												handleChange(
													e
												);
											}
										}}
										tabIndex={
											14
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={3}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.FNR'
										)}
										name='fnr'
										onChange={(
											e: any
										) => {
											const reg =
												/^-?[0-9]*(\.[0-9]*)?$/;
											if (
												!isNaN(
													e
														.target
														.value
												) &&
												reg.test(
													e
														.target
														.value
												)
											) {
												handleChange(
													e
												);
											}
										}}
										tabIndex={
											15
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
								<$Col span={3}>
									<$Input
										size='small'
										label={t(
											'US.COLLECTION.CASE:REALESTATE.SNR'
										)}
										name='snr'
										onChange={(
											e: any
										) => {
											const reg =
												/^-?[0-9]*(\.[0-9]*)?$/;
											if (
												!isNaN(
													e
														.target
														.value
												) &&
												reg.test(
													e
														.target
														.value
												)
											) {
												handleChange(
													e
												);
											}
										}}
										tabIndex={
											16
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
							</$Row>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={6}>
									{!showOnly && (
										<a
											className='d-block'
											style={{
												paddingBottom:
													'0.13rem',
											}}
											onClick={() =>
												openMunicipalityArea()
											}>
											<strong>
												{t(
													'US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYCODE'
												)}
											</strong>
										</a>
									)}
									{showOnly && (
										<strong>
											{t(
												'US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYCODE'
											)}
										</strong>
									)}
									<$Input
										name='municipalityCode'
										size='small'
										value={
											values.municipalityCode
										}
										tabIndex={
											7
										}
										disabled
									/>
								</$Col>
								<$Col span={6}>
									<$Input
										label={t(
											'US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYNAME'
										)}
										name='municipalityArea'
										size='small'
										tabIndex={
											8
										}
										disabled
									/>
								</$Col>
							</$Row>
							<$Row
								className='mb-1'
								gutter={16}>
								<$Col span={12}>
									<$TextArea
										name='comment'
										autoSize={{
											minRows: 4,
											maxRows: 4,
										}}
										size='small'
										label={t(
											'US.COLLECTION.COMMON:COMMON.COMMENT'
										)}
										tabIndex={
											9
										}
										disabled={
											showOnly
										}
									/>
								</$Col>
							</$Row>

							{!showOnly && (
								<div className='drawer-footer-fixed align-content-center justify-content-end'>
									<div>
										<$Button
											className='mr-2'
											onClick={(
												e: any
											) =>
												handleSubmit(
													e
												)
											}
											disabled ={!restProps.isValid}
											type='primary'>
											{drawerInfo?.isEdit
												? t(
														'US.COLLECTION.COMMON:COMMON.UPDATE'
												  )
												: t(
														'US.COLLECTION.COMMON:COMMON.SAVE'
												  )}
										</$Button>
										{restProps.dirty && (
											<$Popconfirm
												title={t(
													'US.COLLECTION.COMMON:COMMON.CANCEL_ERROR'
												)}
												placement='topLeft'
												onConfirm={() =>
													handleCancel(
														resetForm
													)
												}
												okText={t(
													'US.COLLECTION.COMMON:COMMON.YES'
												)}
												cancelText={t(
													'US.COLLECTION.COMMON:COMMON.NO'
												)}>
												<$Button>
													{t(
														'US.COLLECTION.COMMON:COMMON.CANCEL'
													)}
												</$Button>
											</$Popconfirm>
										)}
										{!restProps.dirty && (
											<$Button
												onClick={() =>
													handleCancel(
														resetForm
													)
												}>
												{t(
													'US.COLLECTION.COMMON:COMMON.CANCEL'
												)}
											</$Button>
										)}
									</div>
								</div>
							)}

							{/* Postal Area ---------------------------------------------------------------------------------*/}
							<$Drawer
								title={
									drawerName
								}
								width={800}
								visible={
									postalDrawerVisible
								}
								onClose={
									closePostalArea
								}
								destroyOnClose>
								<PostalArea
									postalAreaClose={() =>
										postalAreaClose()
									}
									drawerContentChangeClick={(
										e: any
									) =>
										handlePostalDoubleClick(
											e?.updatedInfo,
											restProps,
											values
										)
									}
									{...props}
								/>
							</$Drawer>
							{/* Municipality Area ---------------------------------------------------------------------------------*/}
							<$Drawer
								title={
									drawerName
								}
								width={800}
								visible={
									municipalityDrawerVisible
								}
								onClose={
									closeMunicipalityArea
								}
								destroyOnClose>
								<SelectMunicipal
									{...props}
									onCancelClick={() =>
										closeMunicipalityArea()
									}
									currentStep
									drawerContentChangeClick={() => {}}
									onRowClickMuniciples={(
										e: any
									) =>
										handleMunicipalityDoubleClick(
											e,
											restProps
										)
									}
								/>
							</$Drawer>
						</$Skeleton>
					</$Form>
				</>
			)}
		</Formik>
	);
};

const mapStateToProps = (state: RootState) => {
	const { common, domainView, entityDetails, assets } = state;
	const { currentLanguage, currentDateFormat, currentCurrency } = common;
	const { metaData } = domainView;
	const { postalAreas, drawerName } = entityDetails;
	const { drawerInfo, realEstateAssets, isREFetching } = assets;

	return {
		currentLanguage,
		currentDateFormat,
		currentCurrency,
		metaData,
		postalAreas,
		drawerInfo,
		realEstateAssets,
		isREFetching,
		drawerName,
	};
};

const mapDispatchToProps = {
	addRealEstateAssets: AssetsAction.AddRealEstateAssets.save,
	changeDrawerInfo: AssetsAction.AddRealEstateAssets.openDrawer,
	getRealEstateAssets: AssetsAction.RealEstateAssets.get,
	resetRealEstateAssets: AssetsAction.RealEstateAssets.reset,
	updateRealEstateAssets: AssetsAction.UpdateRealEstateAssets.update,
	getPostalDetails: AssetsAction.postalDetails.get,
};

export default connect(mapStateToProps, mapDispatchToProps)(RealEstate);
